import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import { passwordService } from '@/services/password.service';
import { generateRequestId, encryptAES } from '@/utils/crypto';
import toast from 'react-hot-toast';
import { Mail, ArrowRight, CheckCircle, ArrowLeft } from 'lucide-react';
import useApplyUITheme, { useSiteTheme } from '@/hooks/useApplyUITheme';

const forgotPasswordSchema = yup.object().shape({
  email: yup
    .string()
    .email('Invalid email address')
    .required('Email is required'),
});

type ForgotPasswordFormData = {
  email: string;
};

export default function ForgotPassword() {
  useApplyUITheme('auth');
  const { saved } = useSiteTheme('auth');
  const primaryColor = saved?.primary_color;

  const [emailSent, setEmailSent] = useState(false);

  const [submittedEmail, setSubmittedEmail] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(forgotPasswordSchema),
  });

  // Cache Spline assets only for this page; OneSignal owns root scope.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker
      .register('/spline-sw.js', { scope: '/forgot-password' })
      .catch((err) => console.warn('[Spline SW] register failed:', err));
  }, []);

  const forgotPasswordMutation = useMutation({
    mutationFn: (email: string) => {
      const requestId = generateRequestId();
      const encryptedEmail = encryptAES(email, requestId);
      return passwordService.forgotPassword(encryptedEmail, requestId);
    },
    onSuccess: (result, email) => {
      setEmailSent(true);
      setSubmittedEmail(email);
      toast.success('Password reset instructions sent!');
    },
    onError: () => {
      // We still show success to prevent email enumeration
      setEmailSent(true);
      toast.success(
        'If an account exists with this email, password reset instructions have been sent.',
      );
    },
  });

  const onSubmit = (data: ForgotPasswordFormData) => {
    forgotPasswordMutation.mutate(data.email);
  };

  const handleResend = () => {
    if (submittedEmail) {
      const requestId = generateRequestId();
      const encryptedEmail = encryptAES(submittedEmail, requestId);
      passwordService.forgotPassword(encryptedEmail, requestId);
      toast.success('Password reset email resent!');
    }
  };

  if (emailSent) {
    return (
      <div className="w-full">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900">Check Your Email</h2>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-green-800 mb-2">
                We've sent password reset instructions to{' '}
                <strong>{submittedEmail}</strong>.
              </p>
              <p className="text-sm text-gray-600">
                Please check your email and follow the instructions to reset
                your password. The link will expire in 1 hour.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">
              <strong>Didn't receive the email?</strong>
            </p>
            <ul className="text-sm text-gray-600 space-y-1 mb-3">
              <li>• Check your spam or junk folder</li>
              <li>• Make sure you entered the correct email address</li>
              <li>• Wait a few minutes for the email to arrive</li>
            </ul>
            <button
              onClick={handleResend}
              disabled={forgotPasswordMutation.isPending}
              className={`text-sm font-medium ${
                !primaryColor ? 'text-primary-700 hover:text-primary-800' : ''
              }`}
              style={primaryColor ? { color: primaryColor } : undefined}
            >
              Resend password reset email
            </button>
          </div>

          <div className="text-center">
            <Link
              to="/login"
              className={`inline-flex items-center gap-2 text-sm font-medium ${
                !primaryColor ? 'text-primary-700 hover:text-primary-800' : ''
              }`}
              style={primaryColor ? { color: primaryColor } : undefined}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900">
          Forgot Your Password?
        </h2>
        <p className="text-gray-600 mt-2">
          No worries! Enter your email and we'll send you instructions to reset
          your password.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              {...register('email')}
              type="email"
              className="input-field pl-10"
              placeholder="Enter your registered email"
              autoComplete="email"
            />
          </div>
          {errors.email && (
            <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting || forgotPasswordMutation.isPending}
          className="btn-primary w-full flex items-center justify-center gap-2"
          style={primaryColor ? { backgroundColor: primaryColor } : undefined}
        >
          {isSubmitting || forgotPasswordMutation.isPending ? (
            <>
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Sending Instructions...
            </>
          ) : (
            <>
              Send Reset Instructions
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>

        <div className="text-center text-sm text-gray-600">
          Remember your password?{' '}
          <Link
            to="/login"
            className={`font-medium ${
              !primaryColor ? 'text-primary-700 hover:text-primary-800' : ''
            }`}
            style={primaryColor ? { color: primaryColor } : undefined}
          >
            Sign in here
          </Link>
        </div>
      </form>
    </div>
  );
}
